import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Brand } from "../components/Brand";
import { useSEO } from "../lib/seo";

export default function CustomerLanding() {
    useSEO({
        title: "Luxurio Edge Technical Services — Premium On-Demand Home Services in UAE",
        description: "LETS — Premium on-demand home services for the UAE. AC, plumbing, electrical, cleaning, IT and 12+ categories. Vetted technicians, transparent AED pricing, OTP-secure jobs. Available in Dubai, Abu Dhabi, Sharjah, Ajman.",
        canonical: "https://luxurioedgetechnical.com/",
    });
    const { user } = useAuth();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#FAFAF8] text-[#111]">
            {/* Top bar */}
            <header className="absolute top-0 left-0 right-0 z-30 px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between">
                <Brand light size="medium" />
                <nav className="flex items-center gap-3">
                    <Link
                        data-testid="landing-contact-link"
                        to="/contact"
                        className="hidden sm:inline-block text-sm font-medium text-white hover:text-[#D4AF37] transition px-3 py-2.5"
                    >
                        Contact
                    </Link>
                    {user ? (
                        <button
                            data-testid="landing-dashboard-btn"
                            onClick={() => navigate(user.role === "customer" ? "/customer" : user.role === "technician" ? "/technician" : "/admin")}
                            className="lx-btn-primary px-5 py-2.5 text-sm font-medium"
                        >
                            My Dashboard →
                        </button>
                    ) : (
                        <>
                            <Link
                                data-testid="landing-login-link"
                                to="/login"
                                className="text-sm font-medium text-white hover:text-[#D4AF37] transition px-4 py-2.5 border border-white/30 hover:border-[#D4AF37]"
                            >
                                Sign in
                            </Link>
                            <Link
                                data-testid="landing-cta-book"
                                to="/customer"
                                className="lx-btn-primary px-5 py-2.5 text-sm font-medium"
                            >
                                Browse Services
                            </Link>
                        </>
                    )}
                </nav>
            </header>

            {/* Hero */}
            <section className="relative min-h-[100vh] flex items-center overflow-hidden">
                <div
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage:
                            "url('https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/2eba03b5907160303d253ab9612a23b4c0c36931891302ee36174a6adb6b1fa3.png')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 z-10 bg-gradient-to-br from-[#180A21]/95 via-[#180A21]/85 to-[#180A21]/70" />
                <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-center w-full py-32">
                    <div className="lg:col-span-7 lx-fade-up">
                        <div className="lx-label text-[#D4AF37] mb-6">
                            ★ Premium On-Demand Services
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] mb-6">
                            Home services,
                            <br />
                            <span className="italic lx-gold-text">at your doorstep.</span>
                        </h1>
                        <p className="text-lg text-white/70 max-w-xl mb-10 leading-relaxed">
                            From certified technicians for AC, plumbing, electrical and beyond
                            to deep cleaning and home automation. Trusted across UAE, India,
                            UK, Singapore & beyond — same-day booking, real-time tracking.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link
                                data-testid="hero-book-btn"
                                to="/customer"
                                className="lx-btn-gold px-8 py-4 text-base inline-flex items-center gap-2"
                            >
                                Browse services
                                <span>→</span>
                            </Link>
                            <Link
                                data-testid="hero-explore-btn"
                                to="/login"
                                className="px-8 py-4 text-base text-white border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                            >
                                Sign in
                            </Link>
                        </div>

                        <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
                            <Stat label="Verified Pros" value="12K+" />
                            <Stat label="Avg Rating" value="4.9★" />
                            <Stat label="Cities" value="34" />
                        </div>
                    </div>

                    <div className="lg:col-span-5 hidden lg:block">
                        <div className="grid grid-cols-2 gap-4">
                            <FeatureCard
                                img="https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=900&q=80"
                                label="Home Automation"
                            />
                            <FeatureCard
                                img="https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/db03c7614967f04752bf0b5b63dfaa60b6ff67d57cc91c2d98bdcecf57a52e83.png"
                                label="AC & Appliances"
                                tall
                            />
                            <FeatureCard
                                img="https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/c26ee6dfc705d3aa36932b6800cd0bb66942f69bdeeec2af435f40b33c46dbca.png"
                                label="Deep Cleaning"
                                tall
                            />
                            <FeatureCard
                                img="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80"
                                label="Electrical"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories strip */}
            <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
                <div className="grid lg:grid-cols-12 gap-8 mb-16">
                    <div className="lg:col-span-5">
                        <div className="lx-label text-[#B2912A] mb-3">Our Catalogue</div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#180A21] leading-tight">
                            Every service, <span className="italic">curated</span> for excellence.
                        </h2>
                    </div>
                    <p className="lg:col-span-6 lg:col-start-7 text-[#555] leading-relaxed self-end">
                        Each technician is background-checked, skill-certified, and rated by
                        thousands of customers. Book with the confidence of a
                        concierge service — at fair, transparent prices.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {[
                        ["AC Service & Repair", "https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/db03c7614967f04752bf0b5b63dfaa60b6ff67d57cc91c2d98bdcecf57a52e83.png"],
                        ["Deep Cleaning", "https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/c26ee6dfc705d3aa36932b6800cd0bb66942f69bdeeec2af435f40b33c46dbca.png"],
                        ["Electrical", "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=900&q=80"],
                        ["Plumbing", "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&w=900&q=80"],
                        ["Carpentry", "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=900&q=80"],
                        ["CCTV & Smart Home", "https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=900&q=80"],
                        ["Painting", "https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=900&q=80"],
                        ["Pest Control", "https://images.unsplash.com/photo-1632935190508-3c14fd47fc6c?auto=format&fit=crop&w=900&q=80"],
                    ].map(([name, img]) => (
                        <Link
                            key={name}
                            to="/customer"
                            data-testid={`landing-cat-${name.split(" ")[0].toLowerCase()}`}
                            className="group block relative aspect-[3/4] overflow-hidden"
                        >
                            <img
                                src={img}
                                alt={name}
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#180A21]/95 via-[#180A21]/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                <div className="lx-label text-[#D4AF37] mb-1">Service</div>
                                <div className="font-serif text-xl text-white">{name}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Three apps strip */}
            <section className="bg-[#180A21] text-white py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                    <div className="lx-label text-[#D4AF37] mb-3">One Platform</div>
                    <h2 className="font-serif text-4xl sm:text-5xl mb-16 max-w-2xl">
                        Built for three audiences. Engineered for one outcome.
                    </h2>
                    <div className="grid md:grid-cols-3 gap-px bg-[#2B1438]">
                        <AudienceCard
                            label="For Customers"
                            title="Book in seconds. Track in real-time."
                            body="Browse curated services, pay with wallet or card, chat your technician, rate the work, and re-book in a tap."
                            cta="Browse services"
                            href="/customer"
                            testid="aud-customer"
                        />
                        <AudienceCard
                            label="For Technicians"
                            title="Get jobs. Earn more. Grow."
                            body="Accept work that fits your skills and city. Navigate, complete, get paid — and build a 5-star reputation."
                            cta="Open technician app"
                            href={user?.role === "technician" ? "/technician" : "/login"}
                            testid="aud-tech"
                        />
                        <AudienceCard
                            label="For Operators"
                            title="Command-center for your fleet."
                            body="Manage companies, technicians, bookings, payouts, complaints — with PG Books accounting auto-synced."
                            cta="Open admin panel"
                            href={user?.role === "company_admin" || user?.role === "super_admin" ? "/admin" : "/login"}
                            testid="aud-admin"
                        />
                    </div>
                </div>
            </section>

            <section className="bg-[#0F0614] py-20">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="lx-label text-[#D4AF37] mb-3">Get in Touch</div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-4">
                            Questions? <span className="italic lx-gold-text">We&apos;re listening.</span>
                        </h2>
                        <p className="text-white/60 leading-relaxed">
                            Tell us about your project, your property, or your enquiry —
                            our concierge team responds within one business day.
                        </p>
                    </div>
                    <div className="flex lg:justify-end">
                        <Link
                            to="/contact"
                            data-testid="landing-contact-cta"
                            className="lx-btn-gold px-8 py-4 text-base inline-flex items-center gap-2"
                        >
                            Send us an enquiry →
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#E5E2DC] py-10 text-center text-xs text-[#555] tracking-wider">
                © {new Date().getFullYear()} Luxurio Edge Technical Services LLC. All rights reserved.
            </footer>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="font-serif text-3xl text-[#D4AF37]">{value}</div>
            <div className="lx-label text-white/60 mt-1">{label}</div>
        </div>
    );
}

function FeatureCard({ img, label, tall }) {
    return (
        <div className={`relative overflow-hidden ${tall ? "row-span-2" : ""}`}>
            <img src={img} alt={label} className="w-full h-full object-cover aspect-square" style={tall ? { aspectRatio: "1 / 1.7" } : {}} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#180A21]/80 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3">
                <div className="lx-label text-[#D4AF37]">{label}</div>
            </div>
        </div>
    );
}

function AudienceCard({ label, title, body, cta, href, testid }) {
    return (
        <Link
            to={href}
            data-testid={testid}
            className="bg-[#180A21] p-10 group hover:bg-[#2B1438] transition-colors duration-500"
        >
            <div className="lx-label text-[#D4AF37] mb-4">{label}</div>
            <h3 className="font-serif text-2xl mb-4 group-hover:text-[#D4AF37] transition-colors">
                {title}
            </h3>
            <p className="text-white/60 leading-relaxed mb-6 text-sm">{body}</p>
            <div className="text-[#D4AF37] text-sm font-medium tracking-wider uppercase">
                {cta} →
            </div>
        </Link>
    );
}
